import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { VisitRecord } from '../types/VisitRecord';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with /api/v1 for protected endpoints
const apiClient = axios.create({
  baseURL: API_BASE_URL + '/v1',
});

// Create axios instance for auth endpoints (no /v1)
const authClient = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to both clients
[apiClient, authClient].forEach(client => {
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle 401 errors (redirect to login)
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );
});

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  username: string;
  role: string;
}

export const authApi = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await authClient.post('/auth/login', credentials);
    return response.data;
  },

  verifyToken: async (): Promise<{ valid: boolean; username: string; role: string }> => {
    const response = await authClient.get('/auth/verify');
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  },
};

export const visitApi = {
  // Get active visit records
  getActiveVisits: async (params: {
    startTime?: string;
    endTime?: string;
    organizationId?: string;
    msisdn?: string;
    visitToken?: string;
  }): Promise<VisitRecord[]> => {
    const response = await apiClient.get('/visit-records', { params });
    return response.data;
  },

  // Get archived visit records
  getArchivedVisits: async (params: {
    startTime?: string;
    endTime?: string;
    organizationId?: string;
    msisdn?: string;
    visitToken?: string;
  }): Promise<VisitRecord[]> => {
    const response = await apiClient.get('/visit-records/archived', { params });
    return response.data;
  },

  // Get closed (not yet archived) visit records
  getClosedVisits: async (params: {
    startTime?: string;
    endTime?: string;
    organizationId?: string;
    msisdn?: string;
    visitToken?: string;
  }): Promise<VisitRecord[]> => {
    const response = await apiClient.get('/visit-records/closed', { params });
    return response.data;
  },

  // Manually close a visit (simulate exit event)
  closeVisit: async (visitId: string, exitReason: string = 'manual_close'): Promise<void> => {
    await apiClient.post(`/visit-records/${visitId}/close`, {
      exit_reason: exitReason,
    });
  },

  // Get statistics
  getStatistics: async (): Promise<{
    active_visits: number;
    archived_visits: number;
    total_visits: number;
  }> => {
    const response = await apiClient.get('/visit-records/statistics');
    return response.data;
  },
};
